import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import useLoyaltyBurnExperiment from 'loyalty/hooks/useLoyaltyBurnExperiment'
import useOfferBurnSliderState from 'loyalty/hooks/useOfferBurnSliderState'
import { CLEAR_SEARCH_BURN_BATCH_CALCULATIONS } from 'actions/actionConstants'
import { OfferPageBurnSelectionContext } from 'contexts/OfferPage/OfferPageBurnSelectionContext'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'

interface Props {
  isLoyaltyEnabled: boolean
  InnerWrapper?: React.ComponentType<{ children?: React.ReactNode }>
  selectionMaximumActive?: boolean
}

function OfferPageLoyaltyPointsSlider(props: Props) {
  const {
    isLoyaltyEnabled,
    InnerWrapper = React.Fragment,
    selectionMaximumActive = false,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()
  const { charterBundleEnabled } = useContext(OfferPageStateContext)
  const intl = useIntl()
  const { quote: selectionQuote } = useContext(OfferPageBurnSelectionContext)
  const { isAnyVariant: isBurnEnabled } = useLoyaltyBurnExperiment()

  useEffect(() => {
    dispatch({ type: CLEAR_SEARCH_BURN_BATCH_CALCULATIONS })
  }, [dispatch])

  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const burnSelected = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const hasSelectionMaximum = !!selectionQuote || selectionMaximumActive
  const isMaximumResolved = !hasSelectionMaximum || !burnSelected || selectionQuote?.maximum !== undefined
  const batchCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations ?? {})
  const maxBurnPoints = useMemo(() => {
    if (hasSelectionMaximum) return Math.min(userPoints, selectionQuote?.maximum ?? 0)
    const maxPriceInPoints = Object.values(batchCalculations)
      .reduce((max, result) => Math.max(max, result?.data?.priceInPoints ?? 0), 0)
    return maxPriceInPoints > 0 ? Math.min(userPoints, maxPriceInPoints) : userPoints
  }, [batchCalculations, hasSelectionMaximum, selectionQuote, userPoints])

  const isLoadingMax = hasSelectionMaximum ? selectionQuote?.fetching ?? false : Object.values(batchCalculations).some(c => c?.fetching)

  const onPointsToApplyChange = useCallback((pointsToApply: number | undefined) => {
    if (hasSelectionMaximum && selectionQuote?.maximum === undefined && pointsToApply !== undefined) return
    offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply })
  }, [hasSelectionMaximum, offerPageDispatch, selectionQuote?.maximum])

  const { shouldShow, sliderProps } = useOfferBurnSliderState({
    isLoyaltyEnabled: isLoyaltyEnabled && isBurnEnabled,
    maxBurnPoints,
    isLoadingMax,
    onPointsToApplyChange,
    isMaximumResolved,
    stickyUserChoice: hasSelectionMaximum,
    disabled: charterBundleEnabled || (selectionQuote?.maximum !== undefined && selectionQuote.maximum < MINIMUM_POINTS_TO_APPLY),
  })

  if (!shouldShow) {
    return null
  }

  const sliderContent = <LoyaltyBurnSliderBase {...sliderProps} />

  return (
    <div data-large-gap="">
      <InnerWrapper>{sliderContent}</InnerWrapper>
      {selectionQuote?.failed && <BodyText variant="small" colour="critical" role="alert">{intl.formatMessage({
        id: 'WW5ZdASNQJa5bW11ITSU',
        defaultMessage: 'Points pricing is unavailable. Turn off points to continue with cash.',
        description: 'Recovery guidance beside the Flash package points toggle after the selected package conversion fails',
      })}
      </BodyText>}
    </div>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  isLoyaltyEnabled: getIsLoyaltyEnabled(appState),
}), OfferPageLoyaltyPointsSlider)
